import React from "react";
import {
  AlertIndicator,
  alertTypes,
  ModalButtonsContainer,
  TextButton,
  TextInput,
} from "@SeemplicityDev/shared";
import Tree from "../../../../Components/Tree/Tree";
import { resourceTypes } from "../../../FindingsUtils";
import type { TreeNode } from "../../../../Components/Tree/types";
import { IN_USE_TOOLTIP } from "./scopeTreeUtils";
import "./EditScopeGroupDetails.scss";

type TreeProps = React.ComponentProps<typeof Tree>;

export interface EditScopeGroupDetailsProps {
  name: string;
  onNameChange: (name: string) => void;
  nameError?: string;
  // The scope tree to pick members from, already limited to editable nodes.
  tree: TreeNode[] | null;
  // The group itself and its ancestors: shown, but not selectable as members.
  excludedIds?: string[];
  expandedIds?: string[];
  // Scopes connected to rules/automations/widgets: shown with the warning icon.
  inUseIds?: string[];
  preCheckedNodes?: TreeNode[];
  onMembersChange: NonNullable<TreeProps["onChange"]>;
  // Set when the new name (or a dropped member's) is already taken; disables Change.
  collisionError?: string;
  changeDisabled?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

const CONTENTS_TITLE = "Scope Group Contents";
const CONTENTS_COPY =
  "Select scopes and scope groups to include in this scope group.";
const CONTENTS_NOTE =
  "Previously contained scopes that are no longer part of this group will be moved to the top level.";
const WARNING_MESSAGE =
  "Changing scopes' location alters the scopes hierarchy and may change which resources are targeted by connected rules and automations.";

const EditScopeGroupDetails: React.FC<EditScopeGroupDetailsProps> = ({
  name,
  onNameChange,
  nameError,
  tree,
  excludedIds = [],
  expandedIds = [],
  inUseIds = [],
  preCheckedNodes = [],
  onMembersChange,
  collisionError,
  changeDisabled = false,
  onConfirm,
  onCancel,
}) => {
  const excluded = new Set(excludedIds);
  const inUse = new Set(inUseIds);
  return (
    <div className={"modal-text edit-scope-group-details"}>
      <TextInput
        data-testid={"scope-group-name"}
        label={"Name"}
        labelTop
        required
        value={name}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
          onNameChange(e.target.value)
        }
        error={nameError ? { message: nameError } : null}
        placeholder={"Type a name"}
      />

      <div
        className={`edit-scope-group-details-contents ${nameError ? "has-name-error" : ""}`}
      >
        <div className={"edit-scope-group-details-title"}>{CONTENTS_TITLE}</div>
        <div>
          <div>{CONTENTS_COPY}</div>
          <div>{CONTENTS_NOTE}</div>
        </div>
      </div>

      <div
        className={"edit-scope-group-details-warning"}
        data-testid={"edit-scope-group-warning"}
      >
        <AlertIndicator type={alertTypes.WARNING} content={WARNING_MESSAGE} />
      </div>

      <div>
        <Tree
          data={tree}
          withCheckbox
          isSearchable
          initialExpandLevel={1}
          initialExpandedNodes={expandedIds}
          placeholder={"Type to search..."}
          preCheckedNodes={preCheckedNodes}
          onChange={onMembersChange}
          isSelectableNode={(node) =>
            node.type !== resourceTypes.ROOT && !excluded.has(String(node.id))
          }
          nodeOperations={{ onWarning: (id) => inUse.has(String(id)) }}
          warningTooltip={IN_USE_TOOLTIP}
          data-testid={"scope-group-members-tree"}
        />
        {!!collisionError && (
          <div
            className={"edit-scope-group-details-error"}
            data-testid={"edit-scope-group-collision-error"}
          >
            <i className={"seem-icon seem-icon-error"} />
            <span>{collisionError}</span>
          </div>
        )}
      </div>

      <ModalButtonsContainer>
        <TextButton
          buttonStyle={"secondary"}
          label={"Cancel"}
          onClick={onCancel}
          data-testid={"cancel-edit-scope-group"}
        />
        <TextButton
          buttonStyle={"primary"}
          label={"Change"}
          onClick={onConfirm}
          disabled={
            changeDisabled || !!nameError || !!collisionError || !name.trim()
          }
          data-testid={"confirm-edit-scope-group"}
        />
      </ModalButtonsContainer>
    </div>
  );
};

export default EditScopeGroupDetails;
